import { useCallback } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useTasksContextMaybe } from "./tasks.context";
import { DraftRefsState, DraftRefsTarget } from "./useDraftRefsByTarget";

/**
 * Shared "Add to chat" orchestration: build references for the items, append
 * them to a composer of the open task (main by default), report refs skipped
 * over the limit, then show the chat tab. `buildRefs` must be stable. Callers
 * own their selection state and clear it after calling `addToChat`.
 */
export function useAddToChat<TItem, TRef>({
    draftRefsState,
    maxRefs,
    noun,
    buildRefs,
}: {
    draftRefsState: Pick<DraftRefsState<TRef>, "appendDraftRefs"> | null;
    maxRefs: number;
    noun: string;
    buildRefs: (args: { taskId: string; items: TItem[] }) => TRef[];
}) {
    const tasksCtx = useTasksContextMaybe();
    const toaster = useToaster();

    const canAddToChat = !!draftRefsState && !!tasksCtx?.selectedTaskId;

    const addToChat = useCallback(
        (items: TItem[], target: DraftRefsTarget = "main") => {
            if (!draftRefsState || !tasksCtx?.selectedTaskId) {
                return;
            }
            const refs = buildRefs({ taskId: tasksCtx.selectedTaskId, items });
            if (refs.length === 0) {
                return;
            }
            const { skippedOverLimitCount } = draftRefsState.appendDraftRefs(
                target,
                refs,
            );
            if (skippedOverLimitCount > 0) {
                toaster.error(
                    `You can attach up to ${pluralizeWithCount(noun, maxRefs)} per message; ${pluralizeWithCount(
                        noun,
                        skippedOverLimitCount,
                    )} couldn't be added`,
                );
            }
            tasksCtx.showTaskDetailPanel("chat");
        },
        [draftRefsState, tasksCtx, toaster, maxRefs, noun, buildRefs],
    );

    return { canAddToChat, addToChat };
}
